/**
 * KpiStrip — four-pill summary at the top of the user's "My reports" tab.
 *
 * Computes counts client-side from the same `WidgetReportRow[]` that
 * MineList already fetches; no extra endpoint. Each pill is clickable
 * and toggles a status filter on the list below — matches thePnr's
 * customer-facing pattern (`FeedbackKPICards.tsx`).
 */

import { h } from 'preact'

import type { ReportStatus, WidgetReportRow } from '../types'
import type { StringKey } from './i18n'

export type KpiFilter = 'all' | 'new' | 'in_progress' | 'awaiting_validation' | 'resolved'

interface KpiStripProps {
  rows: WidgetReportRow[]
  filter: KpiFilter
  onFilter: (next: KpiFilter) => void
  strings: Record<StringKey, string>
}

interface Counts {
  new: number
  in_progress: number
  awaiting_validation: number
  resolved: number
  total: number
}

export function computeKpiCounts(rows: WidgetReportRow[]): Counts {
  const counts: Counts = {
    new: 0,
    in_progress: 0,
    awaiting_validation: 0,
    resolved: 0,
    total: 0,
  }
  for (const row of rows) {
    counts.total += 1
    switch (row.status) {
      case 'new':
        counts.new += 1
        break
      case 'in_progress':
        counts.in_progress += 1
        break
      case 'awaiting_validation':
        counts.awaiting_validation += 1
        break
      case 'closed':
      case 'rejected':
      case 'duplicate':
      case 'wontfix':
        counts.resolved += 1
        break
    }
  }
  return counts
}

/**
 * Resolution rate = (awaiting_validation + closed + rejected + duplicate +
 * wontfix) / total. Includes operator-resolved-as-no-action so the
 * percentage reflects "your reports the team has acted on" rather than
 * "your reports that turned into shipped fixes". Returns null if there
 * are no rows yet — avoids showing "0%" before the user has filed
 * anything, which reads as ominous.
 */
export function computeResolutionRate(counts: Counts): number | null {
  if (counts.total === 0) return null
  return Math.round(
    ((counts.awaiting_validation + counts.resolved) / counts.total) * 100,
  )
}

const FILTER_FOR_STATUS: Record<ReportStatus, KpiFilter> = {
  new: 'new',
  in_progress: 'in_progress',
  awaiting_validation: 'awaiting_validation',
  closed: 'resolved',
  rejected: 'resolved',
  duplicate: 'resolved',
  wontfix: 'resolved',
}

export function rowsMatchingFilter(
  rows: WidgetReportRow[],
  filter: KpiFilter,
): WidgetReportRow[] {
  if (filter === 'all') return rows
  return rows.filter((r) => FILTER_FOR_STATUS[r.status] === filter)
}

export function KpiStrip({ rows, filter, onFilter, strings }: KpiStripProps) {
  const counts = computeKpiCounts(rows)
  const resolutionRate = computeResolutionRate(counts)
  const cells: Array<{ key: KpiFilter; label: string; value: string }> = [
    { key: 'new', label: strings['kpi.new'], value: String(counts.new) },
    { key: 'in_progress', label: strings['kpi.in_progress'], value: String(counts.in_progress) },
    {
      key: 'awaiting_validation',
      label: strings['kpi.awaiting_validation'],
      value: String(counts.awaiting_validation),
    },
    {
      key: 'resolved',
      label: strings['kpi.resolution_rate'],
      value: resolutionRate === null ? '—' : `${resolutionRate}%`,
    },
  ]
  return (
    <div class="kpi-strip" role="toolbar">
      {cells.map((c) => {
        const active = filter === c.key
        const toggleTo: KpiFilter = active ? 'all' : c.key
        return (
          <button
            type="button"
            class={`kpi-cell kpi-cell--${c.key}${active ? ' is-active' : ''}`}
            onClick={() => onFilter(toggleTo)}
            aria-pressed={active}
          >
            <span class="kpi-value">{c.value}</span>
            <span class="kpi-label">{c.label}</span>
          </button>
        )
      })}
    </div>
  )
}
